<template>
  <div class="hero">
    <section class="card form-card">
      <h2>Проверка стоимости направления</h2>
      <p class="hint">Введите город отправления и назначения — цена и расстояние определятся автоматически (из базы данных, без расхода лимита ATI).</p>

      <div class="form-row">
        <div class="field field-from">
          <label class="label">Город отправления</label>
          <div class="select-area">
            <button class="dropdown-summary" type="button" @click="toggleFrom">
              <span>{{ fromCity || 'Поиск города' }}</span>
              <span class="chevron">{{ fromOpen ? '▲' : '▼' }}</span>
            </button>
            <div v-if="fromOpen" class="dropdown-menu city-menu">
              <input class="input city-search" :value="fromQuery" @input="onFromInput" placeholder="Поиск города" type="text" />
              <div class="dropdown-hint">{{ fromHint }}</div>
              <div class="options-list">
                <div v-for="opt in fromOptions" :key="opt.city_id || opt.city_name" class="dropdown-option" @click="selectFrom(opt)">
                  <span class="option-text">{{ opt.city_name }}</span>
                  <span class="count">{{ opt.region_name }}</span>
                </div>
                <div v-if="!fromOptions.length" class="empty-options">Нет вариантов</div>
              </div>
            </div>
          </div>
        </div>
        <div class="field field-to">
          <label class="label">Город назначения</label>
          <div class="select-area">
            <button class="dropdown-summary" type="button" @click="toggleTo">
              <span>{{ toCity || 'Поиск города' }}</span>
              <span class="chevron">{{ toOpen ? '▲' : '▼' }}</span>
            </button>
            <div v-if="toOpen" class="dropdown-menu city-menu">
              <input class="input city-search" :value="toQuery" @input="onToInput" placeholder="Поиск города" type="text" />
              <div class="dropdown-hint">{{ toHint }}</div>
              <div class="options-list">
                <div v-for="opt in toOptions" :key="opt.city_id || opt.city_name" class="dropdown-option" @click="selectTo(opt)">
                  <span class="option-text">{{ opt.city_name }}</span>
                  <span class="count">{{ opt.region_name }}</span>
                </div>
                <div v-if="!toOptions.length" class="empty-options">Нет вариантов</div>
              </div>
            </div>
          </div>
        </div>
        <label class="field transport">
          <span class="label">Тип кузова</span>
          <select class="input" v-model="transport">
            <option value="tent">Тент</option>
            <option value="ref">Реф</option>
            <option value="iso">ISO</option>
          </select>
        </label>
        <div class="field field-action">
          <button type="button" class="btn-primary" :disabled="loading || !canCalc" @click="calculate">
            {{ loading ? 'Расчёт...' : 'Рассчитать' }}
          </button>
        </div>
      </div>

      <div class="quick-dirs">
        <span class="quick-dirs-label">Популярные направления:</span>
        <div class="quick-dirs-list">
          <button
            v-for="(d, i) in quickDirections"
            :key="i"
            class="quick-dir"
            @click="pickDirection(d)"
          >{{ d.from }} → {{ d.to }}</button>
        </div>
      </div>

      <div v-if="loading && !result" class="card result-card result-skeleton">
        <div class="sk sk-line sk-lg"></div>
        <div class="sk sk-block"></div>
        <div class="sk sk-grid">
          <div class="sk sk-cell"></div>
          <div class="sk sk-cell"></div>
          <div class="sk sk-cell"></div>
        </div>
      </div>
    </section>

    <section v-if="result" class="card result-card" :class="{ 'is-refreshing': loading }">
      <div class="result-head">
        <h3>{{ result.departure_city }} → {{ result.destination_city }}<span v-if="result.distance" class="dir-distance"> • {{ result.distance }} км</span></h3>
        <div class="badges">
          <span v-if="result.source === 'direct'" class="badge badge-ok">цена найдена</span>
          <span v-else class="badge badge-warn">нет цены</span>
          <span v-if="result.is_priority" class="badge badge-priority">priority</span>
          <span v-else-if="result.last_refresh && result.last_refresh.bucket === 'fact'" class="badge badge-fact">fact</span>
          <span v-else class="badge badge-muted">—</span>
        </div>
      </div>

      <div class="featured" :class="{ 'featured-derived': featuredDerived }">
        <div class="featured-row">
          <div class="featured-main">
            <div class="featured-label">Ставка {{ transportLabel.toLowerCase() }} (за км)</div>
            <div class="featured-value copyable" :title="`Скопировать ставку: ${formatPrice(featuredPrice)} ₽/км`" @click="copyText(featuredPrice, `ставка ${formatPrice(featuredPrice)} ₽/км`)">{{ formatPrice(featuredPrice) }}<span class="featured-unit"> ₽/км</span></div>
            <div class="featured-without">без НДС: {{ formatPrice(featuredWithoutNds) }} ₽/км</div>
            <div class="featured-sub">
              {{ featuredDerived ? 'расчёт из тента' : 'реальная ставка ATI' }}
              <span v-if="featuredRealVariant && featuredRealVariant !== 'both'" class="real-tag">{{ realVariantLabel(featuredRealVariant) }}</span>
              · нажмите на число, чтобы скопировать
            </div>
          </div>
          <div v-if="totalCost != null" class="featured-totals">
            <div class="featured-label">Полная стоимость маршрута</div>
            <div class="featured-total copyable" :title="`Скопировать: ${formatPrice(totalCost)} ₽`" @click="copyText(totalCost, `полная стоимость ${formatPrice(totalCost)} ₽`)">
              <b>{{ formatPrice(totalCost) }} ₽</b> <span class="nds-hint">с НДС</span>
            </div>
            <div class="featured-total-sub">без НДС: <b>{{ formatPrice(totalCostWithoutNds) }} ₽</b></div>
            <div class="featured-total-sub">{{ formatPrice(featuredPrice) }} × {{ result.distance }} км</div>
          </div>
        </div>
      </div>

      <div v-if="baseRate" class="compare-block">
        <div class="compare-item">
          <span class="compare-label">Базовая ставка заказчика</span>
          <b class="copyable" :title="`Скопировать: ${formatPrice(baseRate.cost_with_nds)} ₽`" @click="copyText(baseRate.cost_with_nds, `база ${formatPrice(baseRate.cost_with_nds)} ₽`)">{{ formatPrice(baseRate.cost_with_nds) }} ₽</b>
          <span class="compare-sub">без НДС {{ formatPrice(baseRate.cost_without_nds) }} ₽</span>
        </div>
        <div v-if="margin != null" class="compare-item">
          <span class="compare-label">Маржа ATI vs база</span>
          <b :class="['margin-val', margin >= 0 ? 'margin-positive' : 'margin-negative']">{{ margin >= 0 ? '+' : '' }}{{ margin }}%</b>
          <span class="compare-sub">{{ formatPrice(baseRate.cost_with_nds - totalCost) }} ₽</span>
        </div>
      </div>
      <div v-else-if="baseRateForbidden" class="compare-empty compare-forbidden">Нет прав на просмотр базовых ставок.</div>
      <div v-else-if="!baseRateLoading && canCalc" class="compare-empty">Базовая ставка для направления не задана.</div>

      <div class="prices-grid">
        <div v-for="t in transports" :key="t.key" class="price-cell" :class="{ 'price-real': !t.entry?.is_derived, 'price-derived': t.entry?.is_derived }">
          <div class="price-cell-top">
            <span class="dot" :class="t.entry && !t.entry.is_derived ? 'dot-real' : 'dot-derived'"></span>
            <span class="price-cell-name">{{ t.label }}</span>
            <span v-if="t.entry" class="price-cell-tag">{{ t.entry.is_derived ? 'расчёт' : 'реальная' }}</span>
          </div>
          <div v-if="t.entry" class="price-cell-body">
            <div class="price-line">
              <span>мин</span>
              <span class="price-pair"><b class="copyable" :class="{ 'val-computed': t.entry.realVariant === 'without' }" @click="copyText(t.entry.min_volume_with_nds, `${t.label} мин ${formatPrice(t.entry.min_volume_with_nds)} ₽`)">{{ formatPrice(t.entry.min_volume_with_nds) }}</b><small class="nds" :class="{ 'val-real': t.entry.realVariant === 'without' }">/{{ formatPrice(t.entry.min_volume_without_nds) }}</small></span>
            </div>
            <div class="price-line">
              <span>сред</span>
              <span class="price-pair"><b class="copyable" :class="{ 'val-computed': t.entry.realVariant === 'without' }" @click="copyText(t.entry.avg_volume_with_nds, `${t.label} сред ${formatPrice(t.entry.avg_volume_with_nds)} ₽`)">{{ formatPrice(t.entry.avg_volume_with_nds) }}</b><small class="nds" :class="{ 'val-real': t.entry.realVariant === 'without' }">/{{ formatPrice(t.entry.avg_volume_without_nds) }}</small></span>
            </div>
            <div class="price-line">
              <span>макс</span>
              <span class="price-pair"><b class="copyable" :class="{ 'val-computed': t.entry.realVariant === 'without' }" @click="copyText(t.entry.max_volume_with_nds, `${t.label} макс ${formatPrice(t.entry.max_volume_with_nds)} ₽`)">{{ formatPrice(t.entry.max_volume_with_nds) }}</b><small class="nds" :class="{ 'val-real': t.entry.realVariant === 'without' }">/{{ formatPrice(t.entry.max_volume_without_nds) }}</small></span>
            </div>
            <div class="price-cell-foot">
              с НДС / без НДС · ₽/км
              <span v-if="t.entry.realVariant && t.entry.realVariant !== 'both'" class="real-tag-foot">· {{ realVariantLabel(t.entry.realVariant) }}</span>
            </div>
          </div>
          <div v-else class="price-cell-empty">нет данных</div>
        </div>
      </div>

      <div class="meta">
        <span v-if="result.distance">📏 {{ result.distance }} км</span>
        <span v-if="result.report_date">📅 {{ formatDate(result.report_date) }}</span>
        <span v-if="result.loads_count != null">🚚 {{ result.loads_count }} рейсов</span>
        <span v-if="result.confidence != null">🎯 confidence {{ result.confidence }}</span>
        <span v-if="result.ati_route_id">№ {{ result.ati_route_id }}</span>
      </div>

      <div v-if="result.last_refresh" class="refresh-detail">
        <span class="refresh-detail-title">Обновление цены:</span>
        <span class="badge" :class="result.is_priority ? 'badge-priority' : (result.last_refresh.bucket === 'fact' ? 'badge-fact' : 'badge-muted')">{{ result.last_refresh.bucket }}</span>
        <span class="refresh-status">{{ refreshStatusLabel }}</span>
        <span v-if="result.last_refresh.message" class="refresh-message">— {{ result.last_refresh.message }}</span>
      </div>

      <div class="links">
        <a v-if="canYandexLink" :href="yandexLink" target="_blank" rel="noopener" class="ext-link">🗺 Яндекс.Карты · маршрут {{ result.distance }} км ↗</a>
      </div>

      <div class="history-panel">
        <div class="history-days">
          <span>История цен · период:</span>
          <button v-for="d in [7, 30, 90]" :key="d" :class="['chip', { 'chip-active': days === d }]" @click="changeDays(d)">{{ d }}д</button>
        </div>
        <div v-if="historyLoading" class="chart-skeleton">
          <div class="sk sk-chart"></div>
        </div>
        <PriceHistoryChart v-else :series="historySeries" />
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { pricingLookup, getPricesHistory } from '@/backend/atiPriceCheckApi'
import { searchCities } from '@/backend/distributorRoutingApi'
import { lookupBaseRate } from '@/backend/baseRatesApi'
import { createToaster } from '@meforma/vue-toaster'
import { useErrorHandler } from '@/hooks/errorHandler'
import PriceHistoryChart from './PriceHistoryChart.vue'

const fmt = new Intl.NumberFormat('ru-RU')

const formatDate = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
}

const VAT_RATE = 0.22

function normalizeEntry(entry) {
  if (!entry) return null
  const out = { ...entry }
  const hasWith = entry.avg_volume_with_nds != null
  const hasWithout = entry.avg_volume_without_nds != null
  if (hasWith && !hasWithout) out.realVariant = 'with'
  else if (hasWithout && !hasWith) out.realVariant = 'without'
  else out.realVariant = 'both'
  ;(['min', 'avg', 'max']).forEach((base) => {
    const wKey = `${base}_volume_with_nds`
    const woKey = `${base}_volume_without_nds`
    const w = out[wKey]
    const wo = out[woKey]
    if (w != null && wo == null) out[woKey] = Math.round(w / (1 + VAT_RATE))
    else if (wo != null && w == null) out[wKey] = Math.round(wo * (1 + VAT_RATE))
  })
  return out
}

const realVariantLabel = (rv) => (rv === 'with' ? 'реальная: с НДС' : rv === 'without' ? 'реальная: без НДС' : '')

const TRANSPORTS = [
  { key: 'tent', label: 'Тент' },
  { key: 'ref', label: 'Реф' },
  { key: 'iso', label: 'ISO' },
]

const POPULAR_CITIES = [
  { city_id: null, city_name: 'Москва', region_name: 'Московская область' },
  { city_id: null, city_name: 'Санкт-Петербург', region_name: 'Ленинградская область' },
  { city_id: null, city_name: 'Казань', region_name: 'Республика Татарстан' },
  { city_id: null, city_name: 'Ульяновск', region_name: 'Ульяновская область' },
  { city_id: null, city_name: 'Екатеринбург', region_name: 'Свердловская область' },
  { city_id: null, city_name: 'Новосибирск', region_name: 'Новосибирская область' },
  { city_id: null, city_name: 'Краснодар', region_name: 'Краснодарский край' },
  { city_id: null, city_name: 'Нижний Новгород', region_name: 'Нижегородская область' },
  { city_id: null, city_name: 'Самара', region_name: 'Самарская область' },
  { city_id: null, city_name: 'Уфа', region_name: 'Республика Башкортостан' },
  { city_id: null, city_name: 'Ростов-на-Дону', region_name: 'Ростовская область' },
  { city_id: null, city_name: 'Волгоград', region_name: 'Волгоградская область' },
]

const STORAGE_KEY = 'ati-price-check'

const QUICK_DIRECTIONS = [
  { from: 'Москва', fromRegion: 'Московская область', to: 'Ульяновск', toRegion: 'Ульяновская область' },
  { from: 'Москва', fromRegion: 'Московская область', to: 'Казань', toRegion: 'Республика Татарстан' },
  { from: 'Москва', fromRegion: 'Московская область', to: 'Самара', toRegion: 'Самарская область' },
  { from: 'Москва', fromRegion: 'Московская область', to: 'Екатеринбург', toRegion: 'Свердловская область' },
  { from: 'Санкт-Петербург', fromRegion: 'Ленинградская область', to: 'Москва', toRegion: 'Московская область' },
  { from: 'Краснодар', fromRegion: 'Краснодарский край', to: 'Ростов-на-Дону', toRegion: 'Ростовская область' },
  { from: 'Екатеринбург', fromRegion: 'Свердловская область', to: 'Челябинск', toRegion: 'Челябинская область' },
  { from: 'Нижний Новгород', fromRegion: 'Нижегородская область', to: 'Казань', toRegion: 'Республика Татарстан' },
  { from: 'Москва', fromRegion: 'Московская область', to: 'Ростов-на-Дону', toRegion: 'Ростовская область' },
  { from: 'Москва', fromRegion: 'Московская область', to: 'Волгоград', toRegion: 'Волгоградская область' },
  { from: 'Москва', fromRegion: 'Московская область', to: 'Новосибирск', toRegion: 'Новосибирская область' },
  { from: 'Казань', fromRegion: 'Республика Татарстан', to: 'Екатеринбург', toRegion: 'Свердловская область' },
  { from: 'Самара', fromRegion: 'Самарская область', to: 'Уфа', toRegion: 'Республика Башкортостан' },
  { from: 'Уфа', fromRegion: 'Республика Башкортостан', to: 'Краснодар', toRegion: 'Краснодарский край' },
  { from: 'Екатеринбург', fromRegion: 'Свердловская область', to: 'Уфа', toRegion: 'Республика Башкортостан' },
  { from: 'Новосибирск', fromRegion: 'Новосибирская область', to: 'Омск', toRegion: 'Омская область' },
  { from: 'Краснодар', fromRegion: 'Краснодарский край', to: 'Москва', toRegion: 'Московская область' },
]
const DEFAULTS = {
  fromCity: 'Москва',
  fromRegion: 'Московская область',
  toCity: 'Ульяновск',
  toRegion: 'Ульяновская область',
  transport: 'tent',
  days: 30,
}

function loadStored() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}
  } catch (e) {
    return {}
  }
}

function persistStored(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj))
  } catch (e) {
    // ignore quota / privacy errors
  }
}

export default {
  name: 'DirectionHero',
  components: { PriceHistoryChart },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toaster = createToaster()

    const stored = loadStored()
    const q = route.query
    const fromCity = ref(q.from != null ? String(q.from) : (stored.fromCity || DEFAULTS.fromCity))
    const toCity = ref(q.to != null ? String(q.to) : (stored.toCity || DEFAULTS.toCity))
    const fromRegion = ref(q.from_region != null ? String(q.from_region) : (stored.fromRegion || DEFAULTS.fromRegion))
    const toRegion = ref(q.to_region != null ? String(q.to_region) : (stored.toRegion || DEFAULTS.toRegion))
    const transport = ref(q.transport != null ? String(q.transport) : (stored.transport || DEFAULTS.transport))
    const days = ref(q.days != null ? Number(q.days) : (stored.days || DEFAULTS.days))

    const fromQuery = ref('')
    const toQuery = ref('')
    const fromOptions = ref([])
    const toOptions = ref([])
    const fromOpen = ref(false)
    const toOpen = ref(false)
    const fromLoading = ref(false)
    const toLoading = ref(false)
    let fromTimer = null
    let toTimer = null

    const canCalc = computed(() => !!(fromCity.value.trim() && toCity.value.trim()))
    const formatPrice = (v) => (v == null ? '—' : fmt.format(v))

    const persistAndSync = () => {
      persistStored({
        fromCity: fromCity.value,
        fromRegion: fromRegion.value,
        toCity: toCity.value,
        toRegion: toRegion.value,
        transport: transport.value,
        days: days.value,
      })
      router.replace({
        query: {
          ...(route.query.tab ? { tab: route.query.tab } : {}),
          from: fromCity.value || undefined,
          to: toCity.value || undefined,
          from_region: fromRegion.value || undefined,
          to_region: toRegion.value || undefined,
          transport: transport.value,
          days: days.value,
        },
      })
    }

    const lookupQuery = useQuery({
      queryKey: computed(() => [
        'ati-price-lookup',
        fromCity.value.trim().toLowerCase(),
        fromRegion.value.trim().toLowerCase(),
        toCity.value.trim().toLowerCase(),
        toRegion.value.trim().toLowerCase(),
      ]),
      queryFn: () => pricingLookup({
        departure_city: fromCity.value.trim(),
        departure_region: fromRegion.value,
        destination_city: toCity.value.trim(),
        destination_region: toRegion.value,
        transport_type: null,
      }),
      enabled: computed(() => canCalc.value),
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
    })

    const result = computed(() => lookupQuery.data.value ?? null)
    const loading = computed(() => lookupQuery.isFetching.value)
    watch(() => lookupQuery.error.value, (err) => { if (err) useErrorHandler(err) })

    const calculate = () => {
      if (canCalc.value) lookupQuery.refetch()
    }

    const transports = computed(() =>
      TRANSPORTS.map((t) => ({ ...t, entry: normalizeEntry(result.value?.prices?.[t.key] || null) })),
    )
    const transportLabel = computed(() => {
      const t = TRANSPORTS.find((x) => x.key === transport.value)
      return t ? t.label : transport.value
    })
    const featuredEntry = computed(() => normalizeEntry(result.value?.prices?.[transport.value] || null))
    const featuredRealVariant = computed(() => featuredEntry.value?.realVariant || '')
    const featuredPrice = computed(() => featuredEntry.value?.avg_volume_with_nds ?? null)
    const featuredDerived = computed(() => !!featuredEntry.value?.is_derived)
    const featuredWithoutNds = computed(() => featuredEntry.value?.avg_volume_without_nds ?? null)
    const totalCost = computed(() => {
      const rate = featuredEntry.value?.avg_volume_with_nds
      const dist = result.value?.distance
      if (rate == null || !dist) return null
      return Math.round(rate * dist)
    })
    const totalCostWithoutNds = computed(() => {
      const rate = featuredEntry.value?.avg_volume_without_nds
      const dist = result.value?.distance
      if (rate == null || !dist) return null
      return Math.round(rate * dist)
    })

    const baseRateForbidden = ref(false)
    const baseRateQuery = useQuery({
      queryKey: computed(() => ['ati-base-rate', fromCity.value.trim().toLowerCase(), toCity.value.trim().toLowerCase()]),
      queryFn: async () => {
        baseRateForbidden.value = false
        try {
          return await lookupBaseRate({ from_city: fromCity.value.trim(), to_city: toCity.value.trim() })
        } catch (err) {
          const status = err && err.response && err.response.status
          if (status === 404) return null
          if (status === 403) {
            baseRateForbidden.value = true
            return null
          }
          useErrorHandler(err)
          return null
        }
      },
      enabled: computed(() => canCalc.value),
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 0,
    })
    const baseRate = computed(() => baseRateQuery.data.value ?? null)
    const baseRateLoading = computed(() => baseRateQuery.isFetching.value)
    const margin = computed(() => {
      const base = baseRate.value?.cost_with_nds
      const total = totalCost.value
      if (!base || total == null) return null
      return Math.round(((base - total) / base) * 100)
    })

    const copyText = async (value, label) => {
      const text = String(value ?? '')
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          document.body.removeChild(ta)
        }
        toaster.success(`Скопировано: ${label || text}`)
      } catch (e) {
        toaster.error('Не удалось скопировать')
      }
    }

    const refreshStatusLabel = computed(() => {
      const s = result.value?.last_refresh?.status
      const map = {
        success: 'успешно',
        not_found: 'не найдено',
        budget_exhausted: 'бюджет исчерпан',
        transient_error: 'временная ошибка',
        error: 'ошибка',
        priority_route: 'маршрут в приоритете',
      }
      return map[s] || s || ''
    })

    const canYandexLink = computed(() => !!result.value?.departure_geo_point && !!result.value?.destination_geo_point)
    const yandexLink = computed(() => {
      const r = result.value
      if (!r) return '#'
      return `https://yandex.ru/maps/?rtext=${r.departure_geo_point}~${r.destination_geo_point}&rtt=auto`
    })

    const historyQuery = useQuery({
      queryKey: computed(() => ['ati-price-history', result.value?.ati_route_id, days.value]),
      queryFn: () => getPricesHistory({ ati_route_id: result.value.ati_route_id, days: days.value }),
      enabled: computed(() => !!result.value?.ati_route_id),
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
    })
    const historySeries = computed(() => historyQuery.data.value?.series ?? [])
    const historyLoading = computed(() => historyQuery.isFetching.value)
    watch(() => historyQuery.error.value, (err) => { if (err) useErrorHandler(err) })

    const changeDays = (d) => {
      days.value = d
      persistAndSync()
    }

    const pickDirection = (d) => {
      fromCity.value = d.from
      fromRegion.value = d.fromRegion
      toCity.value = d.to
      toRegion.value = d.toRegion
      transport.value = 'tent'
      persistAndSync()
    }

    const fromHint = computed(() => {
      if (fromLoading.value) return 'Поиск...'
      if (!fromQuery.value.trim()) return fromOptions.value.length ? 'Показаны первые 30 городов' : 'Начните вводить город'
      return fromOptions.value.length ? 'Показаны первые 30 городов' : 'Ничего не найдено'
    })
    const toHint = computed(() => {
      if (toLoading.value) return 'Поиск...'
      if (!toQuery.value.trim()) return toOptions.value.length ? 'Показаны первые 30 городов' : 'Начните вводить город'
      return toOptions.value.length ? 'Показаны первые 30 городов' : 'Ничего не найдено'
    })

    const loadFromOptions = async (rawQ) => {
      fromLoading.value = true
      try {
        const trimmed = (rawQ || '').trim()
        const data = await searchCities(trimmed, 30)
        let list = Array.isArray(data) ? data : (data && data.items ? data.items : [])
        if (!list.length && !trimmed) list = POPULAR_CITIES
        fromOptions.value = list
      } catch (e) {
        fromOptions.value = (rawQ || '').trim() ? [] : POPULAR_CITIES
      } finally {
        fromLoading.value = false
      }
    }
    const loadToOptions = async (rawQ) => {
      toLoading.value = true
      try {
        const trimmed = (rawQ || '').trim()
        const data = await searchCities(trimmed, 30)
        let list = Array.isArray(data) ? data : (data && data.items ? data.items : [])
        if (!list.length && !trimmed) list = POPULAR_CITIES
        toOptions.value = list
      } catch (e) {
        toOptions.value = (rawQ || '').trim() ? [] : POPULAR_CITIES
      } finally {
        toLoading.value = false
      }
    }

    const toggleFrom = () => {
      fromOpen.value = !fromOpen.value
      if (fromOpen.value) {
        fromQuery.value = ''
        if (fromOptions.value.length === 0) loadFromOptions('')
      }
    }
    const toggleTo = () => {
      toOpen.value = !toOpen.value
      if (toOpen.value) {
        toQuery.value = ''
        if (toOptions.value.length === 0) loadToOptions('')
      }
    }

    const onFromInput = (e) => {
      clearTimeout(fromTimer)
      const val = e.target.value
      fromQuery.value = val
      fromTimer = setTimeout(() => {
        if (!val.trim()) {
          fromOptions.value = []
          return
        }
        loadFromOptions(val)
      }, 300)
    }
    const onToInput = (e) => {
      clearTimeout(toTimer)
      const val = e.target.value
      toQuery.value = val
      toTimer = setTimeout(() => {
        if (!val.trim()) {
          toOptions.value = []
          return
        }
        loadToOptions(val)
      }, 300)
    }

    const selectFrom = (opt) => {
      fromCity.value = opt.city_name
      fromRegion.value = opt.region_name || ''
      fromQuery.value = ''
      fromOptions.value = []
      fromOpen.value = false
      persistAndSync()
    }
    const selectTo = (opt) => {
      toCity.value = opt.city_name
      toRegion.value = opt.region_name || ''
      toQuery.value = ''
      toOptions.value = []
      toOpen.value = false
      persistAndSync()
    }

    const onClickOutside = (e) => {
      if (fromOpen.value && !e.target.closest('.field-from')) fromOpen.value = false
      if (toOpen.value && !e.target.closest('.field-to')) toOpen.value = false
    }

    watch(transport, () => persistAndSync())

    const resolveRegion = async (cityName) => {
      try {
        const opts = await searchCities(cityName, 5)
        const list = Array.isArray(opts) ? opts : (opts && opts.items ? opts.items : [])
        const exact = list.find((c) => c.city_name && c.city_name.toLowerCase() === cityName.toLowerCase())
        const m = exact || list[0]
        return m ? (m.region_name || '') : ''
      } catch (e) {
        return ''
      }
    }

    watch(
      () => [route.query.from, route.query.to, route.query.from_region, route.query.to_region, route.query.transport],
      async ([f, t, fr, tr, tp]) => {
        let changed = false
        if (f != null && f !== fromCity.value) { fromCity.value = String(f); changed = true }
        if (t != null && t !== toCity.value) { toCity.value = String(t); changed = true }
        if (tp != null && tp !== transport.value) { transport.value = String(tp); changed = true }
        if (!changed) return
        if (fr) fromRegion.value = String(fr)
        else if (fromCity.value) fromRegion.value = await resolveRegion(fromCity.value)
        if (tr) toRegion.value = String(tr)
        else if (toCity.value) toRegion.value = await resolveRegion(toCity.value)
        persistStored({
          fromCity: fromCity.value,
          fromRegion: fromRegion.value,
          toCity: toCity.value,
          toRegion: toRegion.value,
          transport: transport.value,
          days: days.value,
        })
      },
    )

    onMounted(() => {
      window.addEventListener('click', onClickOutside)
      persistAndSync()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('click', onClickOutside)
      clearTimeout(fromTimer)
      clearTimeout(toTimer)
    })

    return {
      fromCity,
      toCity,
      transport,
      days,
      loading,
      result,
      canCalc,
      formatPrice,
      formatDate,
      transports,
      transportLabel,
      featuredPrice,
      featuredWithoutNds,
      featuredDerived,
      featuredRealVariant,
      realVariantLabel,
      totalCost,
      totalCostWithoutNds,
      baseRate,
      baseRateLoading,
      baseRateForbidden,
      margin,
      copyText,
      refreshStatusLabel,
      canYandexLink,
      yandexLink,
      historyLoading,
      historySeries,
      calculate,
      changeDays,
      pickDirection,
      quickDirections: QUICK_DIRECTIONS,
      fromQuery,
      toQuery,
      fromOptions,
      toOptions,
      fromOpen,
      toOpen,
      fromHint,
      toHint,
      toggleFrom,
      toggleTo,
      onFromInput,
      onToInput,
      selectFrom,
      selectTo,
    }
  },
}
</script>

<style scoped>
.hero {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.card {
  padding: 24px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 28px rgba(30, 50, 90, 0.10);
}

h2 {
  margin: 0 0 8px;
  color: #1d2b44;
}

.hint {
  margin: 0 0 18px;
  color: #56657a;
  line-height: 1.5;
}

.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 220px;
}

.field.transport {
  flex: 0 0 160px;
}

.label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 2px;
}

.input {
  padding: 7px 10px;
  border: 1px solid #c3ccdb;
  border-radius: 9px;
  font-size: 15px;
  background: #fff;
  color: #1d2b44;
  box-sizing: border-box;
}

.field select {
  padding: 10px 12px;
  border: 1px solid #c3ccdb;
  border-radius: 9px;
  font-size: 15px;
  background: #f8fbff;
}

.field select:focus {
  outline: none;
  border-color: #3f6fb5;
  background: #fff;
}

.field-from,
.field-to {
  position: relative;
}

.select-area {
  position: relative;
}

.dropdown-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #c3ccdb;
  border-radius: 9px;
  background: #f8fbff;
  color: #1d2b44;
  font-size: 15px;
  cursor: pointer;
  min-height: 20px;
  box-sizing: border-box;
  gap: 6px;
}

.dropdown-summary:hover {
  border-color: #3f6fb5;
}

.chevron {
  flex-shrink: 0;
  color: #3f6fb5;
  font-size: 12px;
}

.dropdown-menu {
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  max-height: 340px;
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #c3ccdb;
  border-radius: 9px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(30, 50, 90, 0.18);
  user-select: none;
}

.city-search {
  margin-bottom: 6px;
  box-sizing: border-box;
  width: 100%;
}

.dropdown-hint {
  margin-bottom: 4px;
  color: #9aa6b5;
  font-size: 12px;
  padding: 0 4px;
}

.options-list {
  display: flex;
  flex-direction: column;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
}

.dropdown-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.dropdown-option:hover {
  background: #eef3fb;
}

.option-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1d2b44;
  font-weight: 500;
}

.count {
  color: #6b7686;
  font-size: 12px;
}

.empty-options {
  color: #9aa6b5;
  font-size: 13px;
  padding: 8px;
}

.btn-primary {
  padding: 11px 22px;
  border: none;
  border-radius: 9px;
  background: #3f6fb5;
  color: #fff;
  font-size: 15px;
  cursor: pointer;
}

.btn-primary:disabled {
  background: #9aa6b5;
  cursor: not-allowed;
}

.result-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.result-head h3 {
  margin: 0;
  color: #1d2b44;
}

.dir-distance {
  color: #6b7686;
  font-weight: 500;
  font-size: 0.82em;
}

.badges {
  display: flex;
  gap: 6px;
}

.badge {
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.badge-ok { background: #e3f6ec; color: #1d7a47; }
.badge-warn { background: #fdf0e3; color: #a85a16; }
.badge-priority { background: #e7e9fb; color: #3a3f9c; }
.badge-fact { background: #eaf3fb; color: #2c5e93; }
.badge-muted { background: #eef1f6; color: #6b7686; }

.copyable {
  cursor: pointer;
  transition: opacity 0.15s;
}

.copyable:hover {
  opacity: 0.68;
  text-decoration: underline dotted;
}

.featured {
  padding: 18px;
  border-radius: 12px;
  background: #eafaf1;
  border: 1px solid #bfe6cf;
  margin-bottom: 16px;
}

.featured-derived {
  background: #f1f3f7;
  border-color: #d3dae6;
}

.featured-row {
  display: flex;
  align-items: stretch;
  gap: 22px;
  flex-wrap: wrap;
}

.featured-main {
  flex: 1 1 200px;
}

.featured-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7686;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 4px;
}

.featured-value {
  font-size: 34px;
  font-weight: 700;
  color: #1d7a47;
  line-height: 1.1;
}

.featured-unit {
  font-size: 16px;
  font-weight: 600;
  color: #2d8a57;
}

.featured-derived .featured-value,
.featured-derived .featured-unit {
  color: #56657a;
}

.featured-without {
  margin-top: 4px;
  color: #56657a;
  font-size: 14px;
}

.featured-totals {
  flex: 1 1 240px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 9px;
  padding: 10px 14px;
}

.featured-total {
  font-size: 16px;
  color: #1d2b44;
  margin: 2px 0;
}

.featured-total b {
  font-size: 24px;
  font-weight: 700;
  color: #1d7a47;
}

.featured-derived .featured-total b {
  color: #56657a;
}

.nds-hint {
  font-size: 12px;
  color: #6b7686;
  font-weight: 500;
}

.featured-total-sub {
  color: #6b7686;
  font-size: 13px;
  margin-top: 2px;
}

.featured-sub {
  margin-top: 12px;
  color: #56657a;
  font-size: 13px;
}

.compare-block {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.compare-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #f4f7fc;
  border: 1px solid #e1e8f1;
  min-width: 180px;
  flex: 1 1 200px;
}

.compare-label {
  font-size: 11px;
  color: #6b7686;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.compare-item b {
  font-size: 22px;
  color: #1d2b44;
  margin-top: 2px;
}

.compare-sub {
  font-size: 12px;
  color: #6b7686;
}

.margin-val {
  font-size: 24px !important;
  font-weight: 700;
}

.margin-positive { color: #1d7a47 !important; }
.margin-negative { color: #b83235 !important; }

.compare-empty {
  padding: 10px 14px;
  margin-bottom: 16px;
  border-radius: 9px;
  background: #f4f7fc;
  color: #9aa6b5;
  font-size: 13px;
}

.prices-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.price-cell {
  padding: 12px;
  border-radius: 10px;
  background: #f8fbff;
  border: 1px solid #e1e8f1;
}

.price-cell.price-real {
  background: #f3fff7;
  border-color: #bfe6cf;
}

.price-cell.price-derived {
  opacity: 0.92;
}

.price-cell-top {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.price-cell-name {
  font-weight: 600;
  color: #1d2b44;
}

.price-cell-tag {
  margin-left: auto;
  font-size: 11px;
  color: #6b7686;
}

.dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.dot-real { background: #2d8a57; }
.dot-derived { background: #b6bfcc; }

.price-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 14px;
  color: #42506a;
}

.price-pair {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
}

.price-line b {
  color: #1d2b44;
}

.nds {
  color: #9aa6b5;
  font-weight: 400;
  font-size: 12px;
}

.price-cell-foot {
  margin-top: 8px;
  font-size: 11px;
  color: #9aa6b5;
  border-top: 1px dashed #e1e8f1;
  padding-top: 5px;
  text-align: right;
}

.val-computed {
  color: #9aa6b5 !important;
  font-weight: 400 !important;
}

.val-real {
  color: #1d7a47 !important;
  font-weight: 700 !important;
  font-size: 13px !important;
}

.real-tag {
  display: inline-block;
  margin: 0 2px;
  padding: 1px 7px;
  border-radius: 9px;
  background: #e3f6ec;
  color: #1d7a47;
  font-size: 11px;
  font-weight: 600;
}

.real-tag-foot {
  color: #1d7a47;
  font-weight: 600;
}

.price-cell-empty {
  color: #9aa6b5;
  font-size: 13px;
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  color: #56657a;
  font-size: 14px;
  margin-bottom: 14px;
}

.refresh-detail {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 14px;
  border-radius: 9px;
  background: #f4f7fc;
  font-size: 13px;
  color: #56657a;
}

.refresh-detail-title {
  font-weight: 600;
  color: #42506a;
}

.refresh-status {
  color: #1d2b44;
  font-weight: 600;
}

.refresh-message {
  color: #9aa6b5;
  font-style: italic;
}

.links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ext-link {
  padding: 8px 14px;
  border-radius: 9px;
  background: #eef3fb;
  color: #284a7c;
  text-decoration: none;
  font-size: 14px;
}

.ext-link:hover { background: #e2ebf8; }

.ext-link-soft {
  background: #f1f3f7;
  color: #42506a;
}

.history-panel {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #eef1f6;
}

.history-days {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #56657a;
  font-size: 14px;
}

.chip {
  padding: 4px 12px;
  border: 1px solid #c3ccdb;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
}

.chip-active {
  background: #3f6fb5;
  color: #fff;
  border-color: #3f6fb5;
}

.history-loading {
  padding: 24px;
  text-align: center;
  color: #56657a;
}

.quick-dirs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
}

.quick-dirs-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.field-action {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.field-action .btn-primary {
  white-space: nowrap;
}

.quick-dirs-label {
  color: #56657a;
  font-size: 13px;
}

.quick-dir {
  padding: 6px 12px;
  border: 1px solid #c3ccdb;
  border-radius: 16px;
  background: #fff;
  color: #284a7c;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.quick-dir:hover {
  border-color: #3f6fb5;
  background: #eef3fb;
}

.is-refreshing {
  position: relative;
  opacity: 0.65;
  pointer-events: none;
}

.is-refreshing::after {
  content: 'Обновление...';
  position: absolute;
  top: 14px;
  right: 18px;
  font-size: 12px;
  color: #3f6fb5;
  pointer-events: none;
}

.result-skeleton {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sk {
  background: linear-gradient(90deg, #eef1f6 25%, #e2e8f1 37%, #eef1f6 63%);
  background-size: 400% 100%;
  border-radius: 8px;
  animation: shimmer 1.4s ease infinite;
}

.sk-line {
  height: 16px;
  width: 40%;
}

.sk-lg {
  height: 26px;
  width: 55%;
}

.sk-block {
  height: 80px;
  width: 100%;
}

.sk-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.sk-cell {
  height: 90px;
}

.chart-skeleton {
  padding: 4px 0;
}

.sk-chart {
  height: 360px;
  width: 100%;
}

@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}

@media (max-width: 720px) {
  .prices-grid {
    grid-template-columns: 1fr;
  }
}
</style>
